/**
 * Performance Card Component
 * KPI card showing single metric with trend indicators
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon, type LucideIcon } from 'lucide-react';
import type { TrendDirection } from '@repo/types';

interface PerformanceCardProps {
  title: string;
  value: string | number;
  change?: number;
  trend?: TrendDirection;
  icon?: LucideIcon;
  description?: string;
  className?: string;
}

export function PerformanceCard({
  title,
  value,
  change,
  trend,
  icon: Icon,
  description,
  className,
}: PerformanceCardProps) {
  const getTrendIcon = () => {
    if (!trend) return null;

    switch (trend) {
      case 'up':
        return <ArrowUpIcon className="h-4 w-4 text-green-600" />;
      case 'down':
        return <ArrowDownIcon className="h-4 w-4 text-red-600" />;
      case 'stable':
        return <MinusIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTrendColor = () => {
    if (!change) return 'text-gray-600';
    return change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600';
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {(change !== undefined || trend || description) && (
          <div className="flex items-center gap-2 mt-1">
            {change !== undefined && (
              <div className={`flex items-center text-xs ${getTrendColor()}`}>
                {getTrendIcon()}
                <span className="ml-1">
                  {change > 0 ? '+' : ''}
                  {change}%
                </span>
              </div>
            )}
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
